import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useBranding } from '@/contexts/BrandingContext'
import { getPageStyles, getCardStyles, getStatusColors } from '@/utils/pageBranding'
import {
    Receipt,
    Search,
    Eye,
    Edit,
    CheckCircle,
    XCircle,
    Clock,
    Calendar,
    Building,
    Plus,
    Loader2,
    AlertCircle,
    Calculator,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { payoutService } from '@/services/payoutService'
import { PaymentPayoutBatch } from '@/types'
import { safeFormatDistanceToNow } from '@/utils/date'

export default function AdminPayoutBatchesList() {
    const { branding } = useBranding()
    const pageStyles = getPageStyles(branding)
    const cardStyles = getCardStyles(branding)
    const statusColors = getStatusColors(branding)
    const navigate = useNavigate()
    const [batches, setBatches] = useState<PaymentPayoutBatch[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [searchTerm, setSearchTerm] = useState('')
    const [statusFilter, setStatusFilter] = useState<string>('')
    const [currencyFilter, setCurrencyFilter] = useState<string>('')
    const [currentPage, setCurrentPage] = useState(1)
    const [totalCount, setTotalCount] = useState(0)
    const [pageSize] = useState(20)

    useEffect(() => {
        fetchBatches()
    }, [currentPage, statusFilter, currencyFilter])

    const fetchBatches = async () => {
        try {
            setLoading(true)
            setError(null)

            const params = {
                page: currentPage,
                page_size: pageSize,
                ...(statusFilter && { status: statusFilter }),
                ...(currencyFilter && { currency: currencyFilter }),
            }

            const response = await payoutService.getPayoutBatches(params)

            if (response.success) {
                setBatches(response.data.results)
                setTotalCount(response.data.count)
            } else {
                setError('Failed to fetch payout batches')
            }
        } catch (err) {
            setError('An error occurred while fetching payout batches')
            console.error('Error fetching payout batches:', err)
        } finally {
            setLoading(false)
        }
    }

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'draft':
                return 'text-gray-600 bg-gray-100 border-gray-200'
            case 'calculated':
                return 'text-blue-600 bg-blue-100 border-blue-200'
            case 'approved':
                return 'text-green-600 bg-green-100 border-green-200'
            case 'paid':
                return 'text-green-700 bg-green-200 border-green-300'
            case 'cancelled':
                return 'text-red-600 bg-red-100 border-red-200'
            default:
                return 'text-gray-600 bg-gray-100 border-gray-200'
        }
    }

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'draft':
                return Clock
            case 'calculated':
                return Calculator
            case 'approved':
                return CheckCircle
            case 'paid':
                return CheckCircle
            case 'cancelled':
                return XCircle
            default:
                return Clock
        }
    }

    const handleBatchAction = async (batchId: number, action: string) => {
        try {
            let response
            switch (action) {
                case 'calculate':
                    response = await payoutService.calculatePayoutBatchTotals(batchId)
                    break
                case 'build_line_items':
                    response = await payoutService.buildPayoutBatchLineItems(batchId)
                    break
                case 'approve':
                    response = await payoutService.approvePayoutBatch(batchId)
                    break
                case 'mark_paid':
                    response = await payoutService.markPayoutBatchPaid(batchId)
                    break
            }

            if (response?.success) {
                // Refresh the list
                await fetchBatches()
            }
        } catch (err) {
            console.error(`Error performing ${action} on batch:`, err)
        }
    }

    const filteredBatches = batches.filter(batch =>
        batch.merchant_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        batch.id.toString().includes(searchTerm)
    )

    const totalPages = Math.ceil(totalCount / pageSize)

    if (loading && batches.length === 0) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={pageStyles.container}>
                <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: branding.primaryColor }} />
                    <p style={pageStyles.textSecondary}>Loading payout batches...</p>
                </div>
            </div>
        )
    }

    return (
        <div className="min-h-screen" style={pageStyles.container}>
            <div className="mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <div>
                                <h1 className="text-3xl font-bold" style={pageStyles.text}>Payout Batches</h1>
                                <p className="mt-1" style={pageStyles.textSecondary}>Manage payout batches and settlements</p>
                            </div>
                        </div>
                        <Button
                            onClick={() => navigate('/admin/payout-batches/create')}
                            className="flex items-center text-white"
                            style={pageStyles.primaryBg}
                        >
                            <Plus className="h-4 w-4 mr-2" />
                            Create Batch
                        </Button>
                    </div>
                </div>

                {/* Filters */}
                <div className="border backdrop-blur-md shadow-sm p-6 mb-6" style={cardStyles}>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-2" style={pageStyles.textSecondary}>Search</label>
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4" style={{ color: branding.textSecondaryColor }} />
                                <Input
                                    placeholder="Search by merchant or ID..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="pl-10 border"
                                    style={{
                                        backgroundColor: branding.surfaceColor,
                                        borderColor: `${branding.textSecondaryColor}30`,
                                        color: branding.textColor
                                    }}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2" style={pageStyles.textSecondary}>Status</label>
                            <select
                                value={statusFilter}
                                onChange={(e) => setStatusFilter(e.target.value)}
                                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                                style={{
                                    backgroundColor: branding.surfaceColor,
                                    borderColor: `${branding.textSecondaryColor}30`,
                                    color: branding.textColor
                                }}
                            >
                                <option value="">All Statuses</option>
                                <option value="draft">Draft</option>
                                <option value="calculated">Calculated</option>
                                <option value="approved">Approved</option>
                                <option value="paid">Paid</option>
                                <option value="cancelled">Cancelled</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-2" style={pageStyles.textSecondary}>Currency</label>
                            <select
                                value={currencyFilter}
                                onChange={(e) => setCurrencyFilter(e.target.value)}
                                className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2"
                                style={{
                                    backgroundColor: branding.surfaceColor,
                                    borderColor: `${branding.textSecondaryColor}30`,
                                    color: branding.textColor
                                }}
                            >
                                <option value="">All Currencies</option>
                                <option value="USD">USD</option>
                                <option value="EUR">EUR</option>
                                <option value="GBP">GBP</option>
                                <option value="CAD">CAD</option>
                            </select>
                        </div>
                        <div className="flex items-end">
                            <Button
                                onClick={() => {
                                    setStatusFilter('')
                                    setCurrencyFilter('')
                                    setSearchTerm('')
                                }}
                                variant="outline"
                                className="w-full"
                                style={{
                                    color: branding.textSecondaryColor,
                                    borderColor: `${branding.textSecondaryColor}40`,
                                    backgroundColor: 'transparent'
                                }}
                            >
                                Clear Filters
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Error Message */}
                {error && (
                    <div className="rounded-lg p-4 mb-6" style={{
                        backgroundColor: statusColors.error.backgroundColor,
                        borderColor: statusColors.error.borderColor,
                        border: '1px solid'
                    }}>
                        <div className="flex items-center">
                            <AlertCircle className="h-5 w-5 mr-2" style={{ color: statusColors.error.color }} />
                            <p style={{ color: statusColors.error.color }}>{error}</p>
                        </div>
                    </div>
                )}

                {/* Batches Table */}
                <div className="border backdrop-blur-md shadow-sm overflow-hidden" style={cardStyles}>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y" style={{ borderColor: `${branding.textSecondaryColor}30` }}>
                            <thead style={{ backgroundColor: `${branding.backgroundColor}80` }}>
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={pageStyles.textSecondary}>
                                        Batch ID
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={pageStyles.textSecondary}>
                                        Merchant
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={pageStyles.textSecondary}>
                                        Period
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={pageStyles.textSecondary}>
                                        Transactions
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={pageStyles.textSecondary}>
                                        Amounts
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={pageStyles.textSecondary}>
                                        Status
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider" style={pageStyles.textSecondary}>
                                        Created
                                    </th>
                                    <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider" style={pageStyles.textSecondary}>
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y" style={{
                                backgroundColor: `${branding.surfaceColor}30`,
                                borderColor: `${branding.textSecondaryColor}30`
                            }}>
                                {filteredBatches.map((batch) => {
                                    const StatusIcon = getStatusIcon(batch.status)
                                    return (
                                        <tr key={batch.id} className="hover:bg-white/5" style={{ backgroundColor: `${branding.primaryColor}05` }}>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium" style={pageStyles.text}>
                                                #{batch.id}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="p-1 rounded mr-2" style={{ backgroundColor: `${branding.primaryColor}20` }}>
                                                        <Building className="h-4 w-4" style={{ color: branding.primaryColor }} />
                                                    </div>
                                                    <div>
                                                        <div className="text-sm font-medium" style={pageStyles.text}>{batch.merchant_name}</div>
                                                        {batch.submerchant_name && (
                                                            <div className="text-sm" style={pageStyles.textSecondary}>{batch.submerchant_name}</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm" style={pageStyles.textSecondary}>
                                                <div className="flex items-center">
                                                    <div className="p-1 rounded mr-1" style={{ backgroundColor: `${branding.secondaryColor}20` }}>
                                                        <Calendar className="h-4 w-4" style={{ color: branding.secondaryColor }} />
                                                    </div>
                                                    <div>
                                                        <div>{new Date(batch.period_start).toLocaleDateString()}</div>
                                                        <div className="text-xs" style={{ color: `${branding.textSecondaryColor}80` }}>to {new Date(batch.period_end).toLocaleDateString()}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm" style={pageStyles.text}>
                                                {batch.total_transactions}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm" style={pageStyles.textSecondary}>
                                                <div>
                                                    <div className="font-medium" style={pageStyles.text}>
                                                        ${Number(batch.net_amount_fiat).toFixed(2)} {batch.currency}
                                                    </div>
                                                    <div className="text-xs" style={{ color: `${branding.textSecondaryColor}80` }}>
                                                        Gross: ${Number(batch.gross_amount_fiat).toFixed(2)}
                                                    </div>
                                                    <div className="text-xs" style={{ color: `${branding.textSecondaryColor}80` }}>
                                                        Commission: ${Number(batch.commission_amount_fiat).toFixed(2)}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(batch.status)}`}>
                                                    <StatusIcon className="h-3 w-3 mr-1" />
                                                    {batch.status.charAt(0).toUpperCase() + batch.status.slice(1)}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm" style={pageStyles.textSecondary}>
                                                <div className="flex items-center">
                                                    <div className="p-1 rounded mr-1" style={{ backgroundColor: `${statusColors.success.backgroundColor}` }}>
                                                        <Calendar className="h-4 w-4" style={{ color: statusColors.success.color }} />
                                                    </div>
                                                    {safeFormatDistanceToNow(batch.created_at, 'Unknown', { addSuffix: true })}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex items-center justify-end space-x-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => navigate(`/admin/payout-batches/${batch.id}`)}
                                                        style={{ color: branding.textSecondaryColor }}
                                                        className="hover:text-white"
                                                    >
                                                        <Eye className="h-4 w-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => navigate(`/admin/payout-batches/${batch.id}/edit`)}
                                                        style={{ color: branding.textSecondaryColor }}
                                                        className="hover:text-white"
                                                    >
                                                        <Edit className="h-4 w-4" />
                                                    </Button>
                                                    {batch.status === 'draft' && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleBatchAction(batch.id, 'calculate')}
                                                            style={{ color: branding.textSecondaryColor }}
                                                            className="hover:text-white"
                                                        >
                                                            <Calculator className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                    {batch.status === 'calculated' && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleBatchAction(batch.id, 'approve')}
                                                            style={{ color: branding.textSecondaryColor }}
                                                            className="hover:text-white"
                                                        >
                                                            <CheckCircle className="h-4 w-4" />
                                                        </Button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Empty State */}
                    {filteredBatches.length === 0 && !loading && (
                        <div className="text-center py-12">
                            <Receipt className="h-12 w-12 mx-auto mb-4" style={{ color: branding.textSecondaryColor }} />
                            <h3 className="text-lg font-medium mb-2" style={pageStyles.text}>No payout batches found</h3>
                            <p className="mb-4" style={pageStyles.textSecondary}>
                                {searchTerm || statusFilter || currencyFilter
                                    ? 'Try adjusting your filters to see more results.'
                                    : 'Get started by creating your first payout batch.'}
                            </p>
                            <Button onClick={() => navigate('/admin/payout-batches/create')} className="text-white" style={pageStyles.primaryBg}>
                                <Plus className="h-4 w-4 mr-2" />
                                Create Payout Batch
                            </Button>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-6">
                        <div className="text-sm" style={pageStyles.textSecondary}>
                            Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalCount)} of {totalCount} results
                        </div>
                        <div className="flex items-center space-x-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                style={{
                                    color: branding.textSecondaryColor,
                                    borderColor: `${branding.textSecondaryColor}40`,
                                    backgroundColor: 'transparent'
                                }}
                            >
                                Previous
                            </Button>
                            <span className="text-sm" style={pageStyles.textSecondary}>
                                Page {currentPage} of {totalPages}
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                                style={{
                                    color: branding.textSecondaryColor,
                                    borderColor: `${branding.textSecondaryColor}40`,
                                    backgroundColor: 'transparent'
                                }}
                            >
                                Next
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
